// @flow
import React from 'react';
import {Link} from '@homeaway/react-localization-components';
import {DualPrice} from '../../DualPrice/DualPrice';
import CancellationMessage from '../../CancellationMessage/CancellationMessage';
import RoomsBedsDetails from './Details/RoomsBedsDetails/RoomsBedsDetails';
import {GeoDetails} from './Details/PropertyDetails/PropertyDetails';
import ColorizedRating from '../../Rating/ColorizedRating/ColorizedRating';
import {FREE_CANCELLATION} from '../../../constants/badges';
import {SuperlativeBadge, NewListingBadge, RankedBadges} from './Badges/Badges';
import WebRatingBadge from '../../WebRatingBadge/WebRatingBadge';

import type {Props as HitInfoProps} from './HitExperimentInfo';

type Props = {
    isPriceAccurateAllowed: boolean,
    hitRedesignTestVariant: number
} & HitInfoProps;

/**
 * Has two different content configurations based on the UK-only
 * `showIncludesTaxesAndFeesMessage` prop - see HitInfo#mapStateToProps for the
 * exact business logic.
 *
 * In the `showIncludesTaxesAndFeesMessage` configuration, a disclaimer is
 * displayed underneath the price, forcing the superlative to move over above
 * the rating stars, which forces the Premier Partner badge up into the badges
 * row with the other badges (free cancellation, rare find, etc).
 */
export default function HitInfoDesktop({
    intl,
    listing,
    hitRedesignTestVariant,
    onClick
}: Props) {
    const {headline} = listing.propertyMetadata || {};
    const rankedBadges = listing.rankedBadges || [];
    const superlativesBadges = listing.superlativesBadges || [];
    const webRatingBadges = listing.webRatingBadges || [];
    const showWebRatingBadge =
        listing.reviewCount === 0 && webRatingBadges && webRatingBadges.length;

    const freeCancellationBadge = rankedBadges.find(
        (e) => e.id === FREE_CANCELLATION
    );

    const DualPriceWrapper = () => {
        return (
            <footer className="media-flex__footer">
                <div className="HitInfo__badgeRatingGroup--old  HitInfo__badgeRatingGroup--left HitInfo__badgeRatingGroup--bottom">
                    <SuperlativeBadge superlativesBadges={superlativesBadges} />
                    <div className="HitExperimentInfo__starRating">
                        {showWebRatingBadge ? (
                            <WebRatingBadge
                                badge={webRatingBadges[0]}
                                renderStarIcon
                            />
                        ) : (
                            <ColorizedRating listing={listing} />
                        )}
                        <RankedBadges rankedBadges={rankedBadges} />
                    </div>
                </div>
                <div className="HitInfo__priceSuperlativeGroup--new HitInfo__badgeRatingGroup--right HitInfo__badgeRatingGroup--bottom">
                    <NewListingBadge superlativesBadges={superlativesBadges} />
                    {freeCancellationBadge && hitRedesignTestVariant === 1 && (
                        <CancellationMessage
                            withIcon={false}
                            freeCancellationBadge={freeCancellationBadge}
                        />
                    )}
                    <DualPrice
                        listing={listing}
                        intl={intl}
                        isDesktop
                        withTooltip={hitRedesignTestVariant === 1}
                        showInstantBookIcon={false}
                    />
                </div>
            </footer>
        );
    };

    return (
        <Link
            target={'_blank'}
            className="media-flex__content"
            href={listing.detailPageUrl}
            onClick={onClick}
            tabIndex="0"
        >
            <div className="media-flex__body HitExperimentInfo__content">
                <GeoDetails listing={listing} />
                <h2
                    className="HitExperimentInfo__headline hover-text"
                    aria-hidden="true"
                >
                    {headline}
                </h2>
                <RoomsBedsDetails listing={listing} />
                {freeCancellationBadge && hitRedesignTestVariant === 2 && (
                    <CancellationMessage
                        withIcon={false}
                        freeCancellationBadge={freeCancellationBadge}
                    />
                )}
            </div>
            <DualPriceWrapper />
        </Link>
    );
}
